import { decodeFunctionResult, encodeFunctionData, multicall3Abi, type Abi, type Address, type Hex } from "viem";
import type { Eip1193Provider } from "./eip1193";
import { readRpc, type RpcReadOptions } from "./read-rpc";

// Runtime matches the official Multicall3 deployment, verified on chain 99901.
export const QUOTE_MULTICALL = {
  address: "0xcA11bde05977b3631167028862bE2a173976CA11" as Address,
  codeHash: "0xd5c15df687b16f2ff992fc8d767b4216323184a2bbc6ee2f9c398c318e770891",
} as const;
export type ContractRead = { to: Address; abi: Abi; functionName: string; args?: readonly unknown[] };

/** Read-only callers opt in after verifying the Multicall runtime at this block.
 * Never route a sender-dependent transaction simulation through this helper. */
export async function readQuoteContracts(provider: Eip1193Provider, calls: readonly ContractRead[], block: Hex,
  guard: () => void, options: RpcReadOptions): Promise<unknown[]> {
  guard(); options.signal?.throwIfAborted();
  const data = calls.map(call => encodeFunctionData(call));
  const raw = await readRpc(provider, { method: "eth_call", params: [{ to: QUOTE_MULTICALL.address,
    data: encodeFunctionData({ abi: multicall3Abi, functionName: "aggregate3", args: [calls.map((call, index) =>
      ({ target: call.to, allowFailure: false, callData: data[index]! }))] }) }, block] }, options);
  guard(); options.signal?.throwIfAborted();
  if (typeof raw !== "string") throw new Error("INVALID_QUOTE_MULTICALL");
  const decoded = decodeFunctionResult({ abi: multicall3Abi, functionName: "aggregate3", data: raw as Hex });
  if (decoded.length !== calls.length || decoded.some(item => !item.success)) throw new Error("INVALID_QUOTE_MULTICALL");
  return decoded.map((item, index) => decodeFunctionResult({ abi: calls[index]!.abi,
    functionName: calls[index]!.functionName, data: item.returnData }));
}
